interface InputItem {
  position: string;
  reactionTime: number | string;
}

export interface InputCaseSession {
  [key: string]: string[];
}

interface Data {
  [key: string]: {
    successCount: number;
    failedCount: number;
  };
}

export function getAdjacentPositions(position: string): string[] {
  const [column, row] = [position[0], parseInt(position.slice(1))];
  
  const adjacentPositions = [
    `${String.fromCharCode(column.charCodeAt(0) - 1)}${row}`, 
    `${String.fromCharCode(column.charCodeAt(0) + 1)}${row}`, 
    `${column}${row - 1}`, 
    `${column}${row + 1}`, 
    `${String.fromCharCode(column.charCodeAt(0) - 1)}${row - 1}`, 
    `${String.fromCharCode(column.charCodeAt(0) + 1)}${row - 1}`, 
    `${String.fromCharCode(column.charCodeAt(0) - 1)}${row + 1}`, 
    `${String.fromCharCode(column.charCodeAt(0) + 1)}${row + 1}`
  ];

  return adjacentPositions.filter(pos => pos.match(/^[A-P](?:1[0-2]|[1-9])$/));
}

export function transformData(input: InputItem[]): Data {
  const results: Data = {};

  input.forEach(item => {
    const { position, reactionTime } = item;

    if (!results[position]) {
      results[position] = { successCount: 0, failedCount: 0 };
    }

    if (typeof reactionTime === 'number') {
      results[position].successCount += 1;
    } else {
      results[position].failedCount += 1;
    }
  });

  return results;
}

export function evolveState(input: InputItem[], inputCaseSession: InputCaseSession): InputCaseSession {
  const data = transformData(input);
  const newState: InputCaseSession = JSON.parse(JSON.stringify(inputCaseSession));

  // Collect all positions with data, including non-target cells
  const allPositions = Array.from(new Set(Object.keys(data).concat(...Object.values(inputCaseSession))));

  allPositions.forEach(position => {
    const adjacentPositions = getAdjacentPositions(position);
    const adjacentData = adjacentPositions
      .map(pos => data[pos] || { successCount: 0, failedCount: 0 }); 

    const posData = data[position] || { successCount: 0, failedCount: 0 };
    const posSuccessRate = posData.successCount + posData.failedCount > 0
      ? posData.successCount / (posData.successCount + posData.failedCount)
      : null;

    const adjacentSuccessRates = adjacentData.map(adj => {
      return adj.successCount + adj.failedCount > 0
        ? adj.successCount / (adj.successCount + adj.failedCount)
        : null;
    });

    const validAdjacentSuccessRates: number[] = adjacentSuccessRates.filter((rate): rate is number => rate !== null);
    const maxAdjacentRate: number | null = validAdjacentSuccessRates.length > 0 ? Math.max(...validAdjacentSuccessRates) : null;
    const minAdjacentRate: number | null = validAdjacentSuccessRates.length > 0 ? Math.min(...validAdjacentSuccessRates) : null;

    // Gestion des cases avec 100% de réussite
    if (posSuccessRate === 1) {
      adjacentPositions.forEach(adjPos => {
        if (inputCaseSession['ZP'].includes(adjPos) && !newState['ZF'].includes(adjPos)) {
          // Ajouter à ZF et retirer de ZP et ZV
          newState['ZF'].push(adjPos);
          newState['ZP'] = newState['ZP'].filter(pos => pos !== adjPos);
          newState['ZV'] = newState['ZV'].filter(pos => pos !== adjPos);
        }
      });

      if (!validAdjacentSuccessRates.length || (minAdjacentRate !== null && minAdjacentRate >= 0.1)) {
        // Retirer de ZF et ZP, ajouter à ZV
        newState['ZF'] = newState['ZF'].filter(pos => pos !== position);
        newState['ZP'] = newState['ZP'].filter(pos => pos !== position);
        if (!newState['ZV'].includes(position)) {
          newState['ZV'].push(position);
        }
      }

    // Gestion des cases avec 0% de réussite
    } else if (posSuccessRate === 0) {
      if (!validAdjacentSuccessRates.length || (maxAdjacentRate !== null && maxAdjacentRate < 0.9)) {
        // Retirer de ZF et ZV, ajouter à ZP
        newState['ZF'] = newState['ZF'].filter(pos => pos !== position);
        newState['ZV'] = newState['ZV'].filter(pos => pos !== position);
        if (!newState['ZP'].includes(position)) {
          newState['ZP'].push(position);
        }
      }
      // Transformation des adjacents en ZV en ZF
      adjacentPositions.forEach(adjPos => {
        if (inputCaseSession['ZV'].includes(adjPos) && !newState['ZF'].includes(adjPos)) {
          // Ajouter à ZF et retirer de ZV et ZP
          newState['ZF'].push(adjPos);
          newState['ZV'] = newState['ZV'].filter(pos => pos !== adjPos);
          newState['ZP'] = newState['ZP'].filter(pos => pos !== adjPos);
        }
      });

    // Gestion des taux > 90% mais < 100%
    } else if (posSuccessRate !== null && posSuccessRate > 0.9) {
      adjacentPositions.forEach(adjPos => {
        if (inputCaseSession['ZP'].includes(adjPos) && !newState['ZF'].includes(adjPos)) {
          // Ajouter à ZF et retirer de ZP et ZV
          newState['ZF'].push(adjPos);
          newState['ZP'] = newState['ZP'].filter(pos => pos !== adjPos);
          newState['ZV'] = newState['ZV'].filter(pos => pos !== adjPos);
        }
      });

    // Gestion des taux < 10% mais non nul
    } else if (posSuccessRate !== null && posSuccessRate < 0.1) {
      adjacentPositions.forEach(adjPos => {
        if (inputCaseSession['ZV'].includes(adjPos) && !newState['ZF'].includes(adjPos)) {
          // Ajouter à ZF et retirer de ZV et ZP
          newState['ZF'].push(adjPos);
          newState['ZV'] = newState['ZV'].filter(pos => pos !== adjPos);
          newState['ZP'] = newState['ZP'].filter(pos => pos !== adjPos);
        }
      });
    }
  });

  // Assurer l'unicité des positions dans chaque zone
  newState['ZP'] = Array.from(new Set(newState['ZP']));
  newState['ZF'] = Array.from(new Set(newState['ZF']));
  newState['ZV'] = Array.from(new Set(newState['ZV']));

  // S'assurer qu'une position n'est présente que dans une seule zone
  newState['ZP'] = newState['ZP'].filter(pos => !newState['ZF'].includes(pos) && !newState['ZV'].includes(pos));
  newState['ZF'] = newState['ZF'].filter(pos => !newState['ZV'].includes(pos));

  return newState;
}

// // // Usage example
// const inputArray = [
//   { "position": "A1", "reactionTime": "Trop long" },
//   { "position": "B1", "reactionTime": 150 },
//   { "position": "B1", "reactionTime": "Trop long" },
//   { "position": "C1", "reactionTime": 300 },
// ];

// const initialState = {
//   "ZF": ["A1", "B1", "C1"],
//   "ZP": ["D1", "E1", "F1"],
//   "ZV": ["A2", "G1", "H1", "I1"]
// };

// const newState = evolveState(inputArray, initialState);
// console.log('Evolved State:', newState);
